<script setup lang="ts">
import type { PortalModalProps } from '.';

import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger
} from '@/components/ui/dialog';
import { cn } from '@/utils';
import { useEventListener } from '@vueuse/core';

defineOptions({
	inheritAttrs: false
});

type Emit = {
	close: [];
	'update:open': [value: boolean];
};

const props = withDefaults(
	defineProps<PortalModalProps & { open?: boolean }>(),
	{
		title: '',
		showCloseIcon: true,
		description: '',
		preventInteractOutside: false,
		open: false
	}
);

const emit = defineEmits<Emit>();

const attrs = useAttrs();

const filteredAttrs = computed(() => {
	// Filtra atributos que não podem ser herdados por Teleport
	const { 'data-test': _dataTest, ...rest } = attrs;
	return rest;
});

function handleInteractOutside(event: Event) {
	if (props.preventInteractOutside) {
		event.preventDefault();
	} else {
		// Close modal when clicking outside
		emit('update:open', false);
		emit('close');
	}
}

function handleOpenChange(open: boolean) {
	emit('update:open', open);
	if (!open) {
		emit('close');
	}
}

useEventListener(window, 'keydown', event => {
	if (event.key === 'Escape') {
		emit('update:open', false);
		emit('close');
	}
});
</script>

<template>
	<Dialog :open="open" @update:open="handleOpenChange">
		<DialogTrigger as-child class="w-full">
			<slot name="trigger"></slot>
		</DialogTrigger>

		<DialogContent
			:class="cn('max-h-[95%]', contentClass)"
			:overlay-class="overlayClass"
			:show-close-icon="showCloseIcon"
			v-bind="{
				...filteredAttrs,
				...(description ? { description } : {})
			}"
			@interact-outside="handleInteractOutside"
		>
			<DialogHeader :class="cn(showCloseIcon ? 'pr-8' : '', headerClass)">
				<slot name="header">
					<DialogTitle
						:class="
							cn(
								'text-wrap font-matter-sq text-2xl font-normal leading-[150%] tracking-tighter text-primary',
								titleClass
							)
						"
					>
						<slot name="title">
							{{ title }}
						</slot>
					</DialogTitle>
					<DialogDescription />
				</slot>
			</DialogHeader>

			<slot></slot>

			<DialogFooter
				:class="cn('flex items-center justify-between', footerClass)"
			>
				<slot name="footer"></slot>
			</DialogFooter>
		</DialogContent>
	</Dialog>
</template>
